import type { ProjectDepartment, TaskGroup } from '../types';
import { resolveTaskGroupIdForDepartment } from './onboardingTaskBridge';
import { DEFAULT_CONTRACTED_TASK_GROUPS } from './taskGroupOrder';

export type ExistingTaskGroupLite = Pick<TaskGroup, 'name' | 'position'> & {
  id?: string;
  projectId?: string;
};

export type MissingContractedTaskGroup = {
  name: string;
  color: string;
  position: number;
  department: ProjectDepartment;
};

/**
 * Contracted-department groups that do not already exist on the project.
 * Uses the same name matching as onboarding task mirroring (Delivery counts
 * as Installation on kit jobs; fuzzy "Install" counts as Installation).
 */
export function missingContractedTaskGroups(
  projectId: string,
  existingGroups: ExistingTaskGroupLite[],
): MissingContractedTaskGroup[] {
  const forProject = existingGroups.filter((g) => !g.projectId || g.projectId === projectId);
  const asGroups: TaskGroup[] = forProject.map((g, i) => ({
    id: g.id || `existing-${i}`,
    projectId,
    name: g.name,
    color: '',
    position: g.position || 0,
  }));

  const missing = DEFAULT_CONTRACTED_TASK_GROUPS.filter(
    (def) => !resolveTaskGroupIdForDepartment(def.department, projectId, asGroups),
  );
  const maxPos = forProject.reduce((m, g) => Math.max(m, g.position || 0), 0);
  return missing.map((def, i) => ({
    name: def.name,
    color: def.color,
    position: maxPos + i + 1,
    department: def.department,
  }));
}

/** Deterministic id so concurrent staff sessions cannot create a second empty group. */
export function reservedContractedTaskGroupId(projectId: string, department: ProjectDepartment): string {
  return `tg-${department}-${projectId}`;
}
